import innerEmitter from '../../inner-emitter/inner-emitter';
import INNER_EVENT from '../../inner-emitter/inner-event';
import loadWorklet from './worklet-loader';
import eventLogger from '../../utils/log/event-log';
import { KIBANA_EVENT } from '../constants';
import { getAudioContext } from '../../utils/audio-context';
import { AudioNodeConnector } from '../../manager/audio-mixer';
import { ILogger } from '../../enums';

/**
 * VolumeMeter class provide basic methods to get audio level
 *
 * @class VolumeMeter
 * @author irisywzhang
 *
 */
class VolumeMeter {
  // 2s 内没收到来自 audioWorklet 的消息，则认为 VolumeMeter 没有在运行
  static get isRunning() {
    return Date.now() - VolumeMeter.lastMessageTime < 2000;
  }
  static lastMessageTime = 0;
  private _volume = 0;
  // 对齐 agora 的音量 db 值
  private _volumeDb = 0;
  private _log: ILogger;
  private _scriptProcessorNode: ScriptProcessorNode | null = null;
  private _audioWorkletNode: AudioWorkletNode | null = null;
  private _interval = 200;
  // Prior to version 66 Google Chrome only supported up to six audio contexts per tab at a time.
  // to avoid running out of context limits, we use a shared context for all VolumeMeter.
  static audioContext = getAudioContext('volume-meter');
  static workletReady: Promise<void>;
  ready = this.preload();
  constructor(options: { log: ILogger; }) {
    const { log } = options;
    this._log = log;
    // ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view?bug_id=1020396022096478911
    innerEmitter.on(INNER_EVENT.AUDIO_LEVEL_INTERVAL, this.handleAudioLevelInterval, this);
  }
  get node() {
    return this._audioWorkletNode || this._scriptProcessorNode;
  }
  preload() {
    if (!VolumeMeter.workletReady) {
      // 压缩工具：https://skalman.github.io/UglifyJS-online/
      const workerJSString = 'class VolumeMeterWorklet extends AudioWorkletProcessor{constructor(){super(),this.volume=0,this.intervalTime=200,this.tick=200,this.isStop=!1,this.cache=[],this.sentFirstInfo1=!1,this.unmute=!1,this.port.onmessage=t=>{var e=t.data;switch(e.name){case"chunk":this.cache.push(...e.data),this.sentFirstInfo1||(this.port.postMessage({cl:e.data.length}),this.sentFirstInfo1=!0);break;case"setIntervalTime":this.intervalTime=e.intervalTime;break;case"unmute":this.unmute=!0;break;case"stop":this.isStop=!0}}}process(t,s){t=t[0],s=s[0];if(t||s){if(this.isStop)return!1;var i=s&&s[0]?s[0].length:0,h=this.cache.length,a=(i<h?(s[0].set(this.cache.slice(0,i)),this.cache=this.cache.slice(i)):this.unmute&&s[0].set(t[0]),(i<h?s:t)[0]);if(a){let e=0;for(let t=0;t<a.length;++t)e=Math.max(Math.abs(a[t]),e);s=a.reduce((t,e)=>t+e*e,0)/a.length;this.volume=e,this.tick-=a.length,this.tick<0&&(this.tick+=this.intervalTime/1e3*sampleRate,this.port.postMessage({volume:this.volume,volumeDb:Math.max(10*Math.log10(s)+100,0)/100,cacheLen:h,outputLen:i}))}}return!0}}registerProcessor("volume-meter",VolumeMeterWorklet);';
      VolumeMeter.workletReady = loadWorklet(VolumeMeter.audioContext, URL.createObjectURL(new Blob([workerJSString], { type: 'application/javascript' })));
    }
    return VolumeMeter.workletReady.then(() => this.initAudioWorklet()).catch(error => {
      this._log.error(`volumeMeter preload error: ${error}`);
      return this.initScriptProcessor();
    });
  }

  initAudioWorklet() {
    if (this._audioWorkletNode) {
      return;
    }
    try {
      this._audioWorkletNode = new AudioWorkletNode(VolumeMeter.audioContext, 'volume-meter');
      let logged = false;
      this._audioWorkletNode.port.onmessage = event => {
        VolumeMeter.lastMessageTime = Date.now();
        this._volume = event.data.volume || 0.0;
        this._volumeDb = event.data.volumeDb || 0;
        if (!logged && event.data.cacheLen && event.data.outputLen) {
          this._log.warn('worklet play success');
          logged = true;
        }
      };
      this.handleAudioLevelInterval({ interval: this._interval });
    } catch (error) {
      this._log.error(`volumeMeter init audio worklet error: ${error}`);
      eventLogger.logFailedEvent({
        userId: this._log.userId,
        eventType: KIBANA_EVENT.LOAD_WORKLET,
        error
      });
      this.initScriptProcessor();
    }
  }

  initScriptProcessor() {
    if (this._scriptProcessorNode) {
      return;
    }
    try {
      this._scriptProcessorNode = getAudioContext('volume-meter').createScriptProcessor(2048, 1, 1);
      this._scriptProcessorNode.onaudioprocess = event => {
        VolumeMeter.lastMessageTime = Date.now();

        const channel = event.inputBuffer.getChannelData(0);
        let sum = 0.0;
        for (let i = 0; i < channel.length; ++i) {
          sum += channel[i] * channel[i];
        }
        this._volume = Math.sqrt(sum / channel.length) || 0.0;
      };
    } catch (error) {
      this._log.error(`volumeMeter init script processor error: ${error}`);
    }
  }

  destroy() {
    if (this._scriptProcessorNode) {
      this._scriptProcessorNode.onaudioprocess = null;
    }
    if (this._audioWorkletNode) {
      this._audioWorkletNode.port.postMessage({
        name: 'stop'
      });
      this._audioWorkletNode.port.onmessage = null;
    }
    this._audioWorkletNode = null;
    this._scriptProcessorNode = null;
    innerEmitter.off(INNER_EVENT.AUDIO_LEVEL_INTERVAL, this.handleAudioLevelInterval, this);
  }

  // /**
  //  * AudioContext 受浏览器自动播放策略限制，当恢复播放时
  //  * 需要 AudioContext.resume 或者重新创建 AudioContext
  //  * 否则会导致，音频恢复播放后，有声音，但是因 AudioContext 不可用，
  //  * 导致 getAudioLevel 值为 0
  //  * @private
  //  * @memberof VolumeMeter
  //  */
  // resume() {
  //   context?.resume();
  // }

  /**
   * 用于获取 0.00000 未丢失精度的音量数据
   * @private
   */
  getInternalAudioLevel() {
    return this._volume;
  }

  getCalculatedVolume() {
    return parseFloat(this._volume.toFixed(2));
  }
  getVolumeDb() {
    return Math.floor(this._volumeDb * 100);
  }

  handleAudioLevelInterval(event: { interval: number; }) {
    const { interval } = event;
    this._interval = interval;
    this._audioWorkletNode?.port.postMessage({
      name: 'setIntervalTime',
      intervalTime: interval
    });
  }
}
export class VolumeMeterConnector extends AudioNodeConnector<AudioWorkletNode> {
  private _volumeMeter: VolumeMeter;
  constructor(options: { log: ILogger; }) {
    super();
    this._volumeMeter = new VolumeMeter(options);
  }
  deleteNode(): void {
    super.deleteNode();
    this._volumeMeter.destroy();
  }
  async init() {
    await this._volumeMeter.preload();
    this.setNode(this._volumeMeter.node as AudioWorkletNode);
  }
  getCalculatedVolume() {
    return this._volumeMeter.getCalculatedVolume();
  }
  getInternalAudioLevel() {
    return this._volumeMeter.getInternalAudioLevel();
  }

  getVolumeDb() {
    return this._volumeMeter.getVolumeDb();
  }
  write(audioData: AudioData) {
    if (this.node) {
      const size = audioData.allocationSize({ planeIndex: 0 });
      const data = new Float32Array(size >> 2);
      audioData.copyTo(data, { planeIndex: 0 });
      this.node.port.postMessage({
        name: 'chunk',
        data
      }, [data.buffer]);
      audioData.close();
    }
  }
}
export default VolumeMeter;
